// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * ActivityLogPage
 *
 * Displays a unified feed of dashboard alerts and insights with filters.
 * Uses dashboard data sources (alerts + insights) merged into a single timeline.
 */

import { useCallback, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { StatusBanner } from '@loa/ui-components';
import { useAdvisor } from '../contexts/AdvisorContext';
import { useAdminViewToggle } from '../hooks/useAdminViewToggle';
import {
  useDashboardAlerts,
  useDashboardInsights,
  useDismissActivityItems,
} from '../hooks/useDashboard';
import { AdvisorFilter } from '../components/AdvisorFilter';
import { ActivityItemCard } from '../components/activity/ActivityItemCard';
import { ActivityLogSkeleton } from '../components/activity/ActivityLogSkeleton';
import { ActivityFilters } from '../components/activity/ActivityFilters';
import { ActivityLogSelectionBar } from '../components/activity/ActivityLogSelectionBar';
import {
  mergeAlertsAndInsights,
  filterActivityItems,
} from '../types/activityLog';
import type {
  ActivityLogFilters,
  ActivityTypeFilter,
  UnifiedActivityItem,
} from '../types/activityLog';
import './ActivityLogPage.css';

// ============================================
// Main Component
// ============================================

export function ActivityLogPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { advisor, loading: advisorLoading, error: advisorError } = useAdvisor();

  // Admin view toggle
  const { isAdminView, isInitializing } = useAdminViewToggle({
    storageKey: 'activityLog',
  });

  // Advisor filter (admin view)
  const [selectedAdvisorId, setSelectedAdvisorId] = useState<string>('all');

  const all = isAdminView && selectedAdvisorId === 'all';
  const advisorId =
    isAdminView && selectedAdvisorId !== 'all' ? selectedAdvisorId : undefined;

  // Data hooks
  const {
    data: alertsData,
    loading: alertsLoading,
    error: alertsError,
    refetch: refetchAlerts,
  } = useDashboardAlerts({
    all,
    advisorId,
    limit: 100,
    skip: isInitializing,
  });

  const {
    data: insightsData,
    loading: insightsLoading,
    error: insightsError,
    refetch: refetchInsights,
  } = useDashboardInsights({
    all,
    advisorId,
    limit: 100,
    skip: isInitializing,
  });

  const {
    dismiss,
    loading: dismissing,
    error: dismissError,
  } = useDismissActivityItems();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const loading = alertsLoading || insightsLoading || advisorLoading || isInitializing;
  const errorMessage = (() => {
    if (alertsError && insightsError) return 'Failed to load Alerts and Insights';
    if (alertsError) return 'Failed to load Alerts';
    if (insightsError) return 'Failed to load Insights';
    if (dismissError) return 'Failed to delete selected items. Please try again.';
    return null;
  })();

  // Filters from URL
  const [filters, setFilters] = useState<ActivityLogFilters>({
    typeFilter: (searchParams.get('typeFilter') as ActivityTypeFilter) || 'all',
    startDate: searchParams.get('startDate') || undefined,
    endDate: searchParams.get('endDate') || undefined,
  });

  // Merge and filter
  const allItems = useMemo(
    () =>
      mergeAlertsAndInsights(
        alertsData?.alerts ?? [],
        insightsData?.insights ?? []
      ),
    [alertsData, insightsData]
  );

  const filteredItems = useMemo(
    () => filterActivityItems(allItems, filters),
    [allItems, filters]
  );

  // ============================================
  // Handlers
  // ============================================

  const updateFilters = useCallback(
    (key: keyof ActivityLogFilters, value: string | undefined) => {
      const newFilters: ActivityLogFilters = {
        ...filters,
        [key]: key === 'typeFilter' ? ((value as ActivityTypeFilter) || 'all') : value,
      };
      setFilters(newFilters);

      const params = new URLSearchParams();
      if (newFilters.typeFilter !== 'all') params.set('typeFilter', newFilters.typeFilter);
      if (newFilters.startDate) params.set('startDate', newFilters.startDate);
      if (newFilters.endDate) params.set('endDate', newFilters.endDate);
      setSearchParams(params);
    },
    [filters, setSearchParams]
  );

  const handleClearFilters = useCallback(() => {
    setFilters({ typeFilter: 'all', startDate: undefined, endDate: undefined });
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  const handleItemClick = useCallback(
    (item: UnifiedActivityItem) => {
      if (item.profileId) {
        navigate(`/relationships/${item.profileId}`);
      }
    },
    [navigate]
  );

  const hasActiveFilters =
    filters.typeFilter !== 'all' || filters.startDate || filters.endDate;

  // Only alerts are dismissable — insights are read-only until insight
  // generation ships and there's something an advisor would want to clear.
  const selectableItems = useMemo(
    () => filteredItems.filter((item) => item.source === 'alert'),
    [filteredItems]
  );

  const allSelected =
    selectableItems.length > 0 &&
    selectableItems.every((item) => selectedIds.has(item.id));

  const handleSelectAllChange = useCallback(
    (checked: boolean) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (checked) {
          selectableItems.forEach((item) => next.add(item.id));
        } else {
          selectableItems.forEach((item) => next.delete(item.id));
        }
        return next;
      });
    },
    [selectableItems]
  );

  const handleItemSelectChange = useCallback(
    (id: string, checked: boolean) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (checked) next.add(id);
        else next.delete(id);
        return next;
      });
    },
    []
  );

  const handleDeleteSelected = useCallback(async () => {
    if (selectedIds.size === 0) return;

    const alertIds: string[] = [];
    for (const item of allItems) {
      if (!selectedIds.has(item.id)) continue;
      if (item.source === 'alert') alertIds.push(item.id);
    }

    try {
      await dismiss({ alertIds });
      setSelectedIds(new Set());
      await Promise.all([refetchAlerts(), refetchInsights()]);
    } catch {
      // Error surfaced via dismissError → errorMessage banner; selection retained.
    }
  }, [allItems, selectedIds, dismiss, refetchAlerts, refetchInsights]);

  // ============================================
  // Error / empty advisor states
  // ============================================

  if (advisorError) {
    return (
      <div className="activity-log">
        <StatusBanner variant="error" message={advisorError} visible position="inline" />
      </div>
    );
  }

  if (!advisorLoading && !advisor) {
    return (
      <div className="activity-log">
        <StatusBanner variant="neutral" message="No account found. Please contact your administrator." visible position="inline" />
      </div>
    );
  }

  // ============================================
  // Render
  // ============================================

  return (
    <div className="activity-log">
      <div className="activity-log__card">
        {/* Header */}
        <div className="activity-log__header">
          <h2 className="activity-log__title font-sans text-body-01 font-medium leading-normal text-black !m-0">Activity Log</h2>
        </div>

        {/* Filters */}
        {isAdminView && (
          <div className="activity-log__filters activity-log__filters--admin">
            <div className="activity-log__filter-field">
              <AdvisorFilter
                value={selectedAdvisorId}
                onChange={setSelectedAdvisorId}
                disabled={loading}
              />
            </div>
          </div>
        )}

        <ActivityFilters
          filters={filters}
          onChange={updateFilters}
          onClear={handleClearFilters}
          disabled={loading}
        />

        {/* Error */}
        {errorMessage && (
          <StatusBanner variant="error" message={errorMessage} visible position="inline" />
        )}

        {/* Content */}
        {(() => {
          if (loading) return <ActivityLogSkeleton />;
          if (filteredItems.length === 0) {
            return (
              <div className="activity-log__empty">
                <span className="activity-log__empty-text">
                  {hasActiveFilters
                    ? 'No activity matches your filter criteria. Try adjusting your filters.'
                    : 'No recent activity to show'}
                </span>
              </div>
            );
          }
          return (
            <>
              {selectableItems.length > 0 && (
                <ActivityLogSelectionBar
                  allSelected={allSelected}
                  selectedCount={selectedIds.size}
                  onSelectAllChange={handleSelectAllChange}
                  onDeleteSelected={handleDeleteSelected}
                  disabled={dismissing}
                />
              )}
              <div className="activity-log__list">
                {filteredItems.map((item) => (
                  <ActivityItemCard
                    key={item.id}
                    item={item}
                    onClick={handleItemClick}
                    selectable={item.source === 'alert'}
                    selected={selectedIds.has(item.id)}
                    onSelectChange={handleItemSelectChange}
                  />
                ))}
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );
}

export default ActivityLogPage;
